import React, { useState } from 'react';
import { ButtonGroup } from 'reactstrap';

import ProposalCard from 'src/components/TradeRule/TradeRuleCustomerProposals/ProposalCard';
import {
  TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL, TRADE_RULE_ACCEPT, TRADE_RULE_CANCEL,
  TRADE_RULE_REJECT, TRADE_RULE_STATE_PROPOSED,
} from 'src/util/constants';

import { buildTradeRuleActionButtons } from '../helpers';

/**
 * Segregates trade proposals in to buy and sell with more context.
 * @param {string} id - logged user
 * @param {object} proposals
 * @returns {object} - trade rules (segregated).
 */
export const getTradeRules = (id, proposals) => {
  const rules = {
    buy: [], sell: [], counterPartySell: [], counterPartyBuy: [], count: 0,
  };

  const displayList = [TRADE_RULE_ACCEPT, TRADE_RULE_REJECT, TRADE_RULE_CANCEL];
  if (!id || !proposals || !proposals.edges.length === 0) { return rules; }

  proposals.edges.forEach((ruleEdge) => {
    const { node: ruleNode } = ruleEdge;
    if (ruleNode.state !== TRADE_RULE_STATE_PROPOSED) return;

    const actions = [];
    // tradePoint.id may be '' (sentinel), null, or undefined — treat all as "not yet selected".
    if (ruleNode.buyer?.user?.id === id) {
      if (!ruleNode.buyer.tradePoint?.id) {
        actions.push(TRADE_RULE_REJECT, TRADE_RULE_ACCEPT);
      } else {
        actions.push(TRADE_RULE_CANCEL);
      }
    }
    if (ruleNode.seller?.user?.id === id) {
      if (!ruleNode.seller.tradePoint?.id) {
        actions.push(TRADE_RULE_REJECT, TRADE_RULE_ACCEPT);
      } else {
        actions.push(TRADE_RULE_CANCEL);
      }
    }
    actions.sort((a, b) => displayList.indexOf(a) - displayList.indexOf(b));
    // If the seller is the proposer...

    if (ruleNode.seller?.user?.id === ruleNode.proposedBy?.id) {
      if (id === ruleNode.seller?.user?.id && ruleNode.seller.tradePoint?.id) {
        rules.sell.push({ ...ruleNode, actions });
      } else {
        rules.counterPartySell.push({ ...ruleNode, actions });
      }
    } else if (ruleNode.buyer?.user?.id === ruleNode.proposedBy?.id) {
      if (id === ruleNode.buyer?.user?.id && ruleNode.buyer.tradePoint?.id) {
        rules.buy.push({ ...ruleNode, actions });
      } else {
        rules.counterPartyBuy.push({ ...ruleNode, actions });
      }
    }
  });

  return rules;
};

/**
 * Prepares the list of available action buttons for a trade proposal.
 * @param {object} intl - `react-intl` instance used to format labels.
 * @param {object} rule - Trade rule object.
 * @param {Array<object>} meters - Flat array of the viewer's meter nodes.
 * @param {boolean} metersLoading - Whether the meters are still being fetched.
 * @param {boolean} isSubmitting - Whether the form is currently submitting.
 * @param {Function} onActionStartFunc - Function to call when an action starts.
 * @param {Function} onActionFinishFunc - Function to call when an action finishes.
 * @returns {React.ReactElement} - Action buttons for a trade proposal.
 */
export const buildTradeRuleActionButton = (
  intl,
  rule,
  meters,
  metersLoading,
  isSubmitting,
  onActionStartFunc,
  onActionFinishFunc,
) => {
  const resolveActionLabel = (action) => {
    if (action === TRADE_RULE_ACCEPT) {
      return intl.formatMessage({
        id: 'trade_rule.trade_rule_customer_proposals.trade_rule_action_button.accept.label',
        defaultMessage: 'Accept',
      });
    }
    const title = action.toLowerCase().split('_').splice(-1)[0];
    return title.charAt(0).toUpperCase() + title.slice(1);
  };

  const buttons = buildTradeRuleActionButtons({
    intl,
    rule,
    meters,
    metersLoading,
    isSubmitting,
    onActionStartFunc,
    onActionFinishFunc,
    resolveActionLabel,
  });

  if (!buttons) {
    return null;
  }

  return (
    <ButtonGroup>
      {buttons}
    </ButtonGroup>
  );
};

/**
 * Wraps tradeRuleActionButton with local processing state to prevent double-submit.
 * Each rendered proposal card gets its own independent in-flight guard.
 * @param {object} props - Component props.
 * @param {object} props.intl - React-Intl instance.
 * @param {object} props.rule - Trade rule object.
 * @param {Array<object>} props.meters - Flat array of the viewer's meter nodes.
 * @param {boolean} props.metersLoading - Whether the meters are still being fetched.
 * @returns {React.ReactElement} - Action buttons with double-submit guard.
 */
function TradeRuleActionButtonContainer({
  intl, rule, meters, metersLoading,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  return buildTradeRuleActionButton(
    intl,
    rule,
    meters,
    metersLoading,
    isSubmitting,
    () => setIsSubmitting(true),
    () => setIsSubmitting(false),
  );
}

/**
 * Prepares the list of buy proposal cards.
 * @param {object} intl - `react-intl` instance used to format labels.
 * @param {Array<object>} tradeRules
 * @param {Array<object>} meters - Flat array of the viewer's meter nodes.
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} direction - The trade direction.
 * @param {boolean} metersLoading - Whether the meters are still being fetched.
 * @returns {Array<React.ReactElement>} - List of proposal cards.
 */
export const buildTradeRuleProposalCardList = (
  intl,
  tradeRules,
  meters,
  direction,
  metersLoading,
) => {
  if (!intl || !tradeRules) {
    return null;
  }

  return tradeRules.map((rule) => (
    <ProposalCard
      key={`trade-rules-${rule.id}`}
      rule={rule}
      direction={direction}
      actionButton={(
        <TradeRuleActionButtonContainer
          intl={intl}
          rule={rule}
          meters={meters}
          metersLoading={metersLoading}
        />
)}
    />
  ));
};
